import {
  CircularProgress,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableHead,
  TablePagination,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import React, { useCallback, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableCell,
  TableContainer,
  TableHeaderRow,
  TableRow,
} from "@/components/molecules/Table/styled";
import { getInitialStockMovementFilterValues } from "@/components/templates/SkuVariationForm/constants";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "@/constants/pagination";
import useGetStockMovements from "@/hooks/requests/stock/use-get-stock";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { SkuMovementMessages } from "@/messages/sku/sku-movement.messages";
import { FilterType } from "@/types/filter.types";

import { StockMovementTableRow } from "./row";

interface Props {
  open: boolean;
  onClose: () => void;
  messages: SkuMovementMessages;
  skuId: number;
}

const StockMovementHistoryModal: React.FC<Props> = ({ open, onClose, messages, skuId }) => {
  const pagination = usePagination();
  const { pageNumber, pageSize, changePage, extend } = pagination;
  const filter = useFilters({ initialValue: getInitialStockMovementFilterValues(skuId) });
  const { filters, changeFilters } = filter;
  const { data, isLoading } = useGetStockMovements({ filters, pageNumber, pageSize, skuId });
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const handleApplyDateFilter = () => {
    if (startDate && endDate) {
      const startOfDay = startDate.startOf("day").toISOString();
      const endOfDay = endDate.endOf("day").toISOString();

      changeFilters([
        ...getInitialStockMovementFilterValues(skuId),
        {
          property: "movementDate",
          value: startOfDay,
          filterType: FilterType.GreaterThan,
        },
        {
          property: "movementDate",
          value: endOfDay,
          filterType: FilterType.LessThan,
        },
      ]);
    }
  };

  const handleClearDateFilter = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    changeFilters(getInitialStockMovementFilterValues(skuId));
  }, [skuId, changeFilters]);

  const columns = [
    messages.movementDateLabel,
    messages.typeLabel,
    messages.quantityLabel,
    messages.commentsLabel,
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogContent sx={{ overflowY: "visible" }}>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight={"bold"}>
              {messages.stockMovementHistoryTitle}
            </MDTypography>
          </MDBox>

          <MDBox mt={2}>
            <Grid container spacing={3}>
              <Grid container size={12} sx={{ justifyContent: "space-between" }}>
                <Grid container size={{ xs: 12, md: 8 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <DatePicker
                      label={messages.initialDateLabel}
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      openTo="day"
                      disableFuture
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DatePicker
                      label={messages.finalDateLabel}
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      openTo="day"
                      disableFuture
                    />
                  </Grid>
                </Grid>

                <Grid
                  size={{ xs: 12, md: 3 }}
                  container
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                  gap={1}
                >
                  <MDButton variant="outlined" color="dark" onClick={handleClearDateFilter}>
                    {messages.clearButton}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={handleApplyDateFilter}
                    disabled={!startDate || !endDate}
                  >
                    {messages.filterButton}
                  </MDButton>
                </Grid>
              </Grid>
            </Grid>
          </MDBox>

          <TableContainer sx={{ marginTop: 2 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableHeaderRow>
                  {columns.map((label, index) => (
                    <HeaderTableCell key={`${index}-${label}`}>{label}</HeaderTableCell>
                  ))}
                </TableHeaderRow>
              </TableHead>
              <TableBody>
                {data?.items.length
                  ? data.items.map((movement, index) => (
                      <StockMovementTableRow
                        key={index}
                        movement={movement}
                        index={index}
                        messages={messages}
                      />
                    ))
                  : !isLoading && (
                      <TableRow index={0}>
                        <TableCell colSpan={columns.length} style={{ textAlign: "center" }}>
                          <MDTypography variant="body1">{messages.noDataMessage}</MDTypography>
                        </TableCell>
                      </TableRow>
                    )}
              </TableBody>
            </Table>

            {isLoading && (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            )}
          </TableContainer>

          {data && (
            <TablePagination
              labelRowsPerPage={messages.rowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
              component="div"
              count={data.totalCount}
              onRowsPerPageChange={(e) => extend(Number(e.target.value))}
              rowsPerPage={pageSize}
              page={pageNumber - 1}
              onPageChange={(_, pageNumber) => changePage(pageNumber)}
            />
          )}

          <Grid container justifyContent="flex-end">
            <MDButton variant="gradient" color="dark" onClick={onClose}>
              {messages.exitButton}
            </MDButton>
          </Grid>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
};

export default StockMovementHistoryModal;
